import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Badge } from "@groot/ui/badge";
import { Button } from "@groot/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@groot/ui/card";
import { StatTile } from "@groot/ui/stat-tile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@groot/ui/dialog";
import { Input } from "@groot/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import {
  useDeletePayment,
  usePaymentHistory,
  usePaymentStats,
  useUpdatePayment,
} from "@/hooks/api/useBills";
import { useAccounts } from "@/hooks/api";
import { formatDate } from "@/lib/utils";
import type { BillPayment } from "@/types";
import dayjs from "dayjs";
import { Calendar, DollarSign, Edit2, History, Trash2, TrendingUp } from "lucide-react";
import { useState } from "react";

interface PaymentHistoryProps {
  billId: number;
}

export function PaymentHistory({ billId }: PaymentHistoryProps) {
  const [paymentToDelete, setPaymentToDelete] = useState<BillPayment | null>(null);
  const [paymentToEdit, setPaymentToEdit] = useState<BillPayment | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editAccountId, setEditAccountId] = useState<number | undefined>();

  const { data: payments = [], isLoading: paymentsLoading } = usePaymentHistory(billId);
  const { data: stats, isLoading: statsLoading } = usePaymentStats(billId);
  const { data: accounts = [] } = useAccounts();
  const deletePayment = useDeletePayment();
  const updatePayment = useUpdatePayment();

  const handleDelete = async () => {
    if (paymentToDelete) {
      await deletePayment.mutateAsync({
        id: paymentToDelete.id,
        billId: paymentToDelete.billId,
      });
      setPaymentToDelete(null);
    }
  };

  const openEditDialog = (payment: BillPayment) => {
    setPaymentToEdit(payment);
    setEditAmount(String(payment.amount));
    setEditDate(dayjs(payment.paidDate).format("YYYY-MM-DD"));
    setEditNotes(payment.notes || "");
    setEditAccountId(payment.accountId || undefined);
  };

  const handleUpdate = async () => {
    if (!paymentToEdit) return;

    await updatePayment.mutateAsync({
      id: paymentToEdit.id,
      data: {
        amount: Number.parseFloat(editAmount),
        paidDate: editDate,
        notes: editNotes || undefined,
        accountId: editAccountId,
      },
    });

    setPaymentToEdit(null);
  };

  if (paymentsLoading || statsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="size-5" />
            Payment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-32 items-center justify-center">
            <div className="size-6 animate-spin rounded-full border-2 border-muted border-t-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasPayments = payments.length > 0;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="size-5" />
            Payment History
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Payment Statistics */}
          {hasPayments && stats && (
            <div className="grid grid-cols-3 gap-3">
              <Card size="sm" className="p-3">
                <StatTile
                  label="Total paid"
                  icon={<DollarSign className="size-3.5" />}
                  value={<AmountDisplay amount={stats.totalAmount} />}
                />
                <p className="text-xs text-muted-foreground">
                  {stats.totalPayments} payment{stats.totalPayments > 1 ? "s" : ""}
                </p>
              </Card>

              <Card size="sm" className="p-3">
                <StatTile
                  label="Average"
                  icon={<TrendingUp className="size-3.5" />}
                  value={<AmountDisplay amount={stats.averageAmount} />}
                />
                <p className="text-xs text-muted-foreground">per payment</p>
              </Card>

              <Card size="sm" className="p-3">
                <StatTile
                  label="Last paid"
                  icon={<Calendar className="size-3.5" />}
                  value={<AmountDisplay amount={stats.lastPaymentAmount || 0} />}
                />
                <p className="text-xs text-muted-foreground">
                  {stats.lastPaymentDate
                    ? dayjs(stats.lastPaymentDate).format("MMM D, YYYY")
                    : "N/A"}
                </p>
              </Card>
            </div>
          )}

          {/* Payment List */}
          <div className="space-y-2">
            {!hasPayments ? (
              <div className="rounded-lg border border-dashed p-6 text-center">
                <History className="mx-auto size-8 text-muted-foreground" />
                <p className="mt-2 text-sm font-medium">No payments yet</p>
                <p className="text-xs text-muted-foreground">
                  Payments will appear here once you mark this bill as paid
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-muted-foreground">Recent Payments</h4>
                  <Badge variant="secondary">{payments.length} total</Badge>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent/50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold">
                            <AmountDisplay amount={payment.amount} />
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(dayjs(payment.paidDate).toDate())}
                          </p>
                        </div>
                        {payment.account && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Paid from: {payment.account.accountName}
                          </p>
                        )}
                        {payment.notes && (
                          <p className="text-xs text-muted-foreground mt-1 italic">
                            "{payment.notes}"
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(payment)}
                          title="Edit payment"
                        >
                          <Edit2 className="size-4 text-muted-foreground" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPaymentToDelete(payment)}
                        >
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Payment Dialog */}
      <Dialog open={paymentToEdit !== null} onOpenChange={() => setPaymentToEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
            <DialogDescription>Update the payment details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Amount</label>
              <Input
                type="number"
                step="0.01"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Paid Date</label>
              <Input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Account</label>
              <Select
                value={editAccountId ? String(editAccountId) : "none"}
                onValueChange={(value) =>
                  setEditAccountId(value === "none" ? undefined : Number(value))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={String(account.id)}>
                      {account.accountName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Input
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Optional notes..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentToEdit(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={updatePayment.isPending || !editAmount || !editDate}
            >
              {updatePayment.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={paymentToDelete !== null} onOpenChange={() => setPaymentToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Payment?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this payment record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {paymentToDelete && (
            <div className="rounded-lg border p-3 bg-muted">
              <p className="text-sm font-semibold">
                <AmountDisplay amount={paymentToDelete.amount} />
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDate(dayjs(paymentToDelete.paidDate).toDate())}
              </p>
              {paymentToDelete.notes && (
                <p className="text-xs text-muted-foreground mt-1 italic">
                  "{paymentToDelete.notes}"
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentToDelete(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deletePayment.isPending}>
              {deletePayment.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
